import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "../../config/api";
import "./index.css";

const formatQuantity = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0.00";
  }

  return Number(value).toFixed(2);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  return `$${Number(value).toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "N/A";
  }

  return new Date(value).toLocaleString();
};

const InventoryReportPage = () => {
  const location = useLocation();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchReport = async () => {
      setIsLoading(true);
      setError("");

      try {
        const searchParams = new URLSearchParams(location.search);
        const response = await apiFetch("/api/inventory-reports/printable/", {
          params: Object.fromEntries(searchParams.entries()),
        });

        if (!isMounted) {
          return;
        }

        setReport(response.data);
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        setError("Unable to load the printable inventory report.");
      } finally {
        if (!isMounted) {
          return;
        }

        setIsLoading(false);
      }
    };

    fetchReport();

    return () => {
      isMounted = false;
    };
  }, [location.search]);

  return (
    <div className="inventory-report-page">
      <div className="inventory-report-page__actions">
        <button type="button" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <div className="inventory-report-page__sheet">
        <header className="inventory-report-page__header">
          <div>
            <p>Inventory report</p>
            <h1>Operational inventory snapshot</h1>
          </div>
          {report?.generated_at ? (
            <span>Generated {formatDateTime(report.generated_at)}</span>
          ) : null}
        </header>

        {error ? <div className="inventory-report-page__error">{error}</div> : null}
        {isLoading ? (
          <div className="inventory-report-page__loading">Loading report...</div>
        ) : null}

        {report ? (
          <>
            <section className="inventory-report-page__summary">
              <article>
                <span>Catalog items</span>
                <strong>{report.summary?.total_items || 0}</strong>
              </article>
              <article>
                <span>Active locations</span>
                <strong>{report.summary?.active_locations || 0}</strong>
              </article>
              <article>
                <span>Stock rows</span>
                <strong>{report.summary?.stock_rows || 0}</strong>
              </article>
              <article>
                <span>Total units</span>
                <strong>{formatQuantity(report.summary?.total_units)}</strong>
              </article>
            </section>

            <section className="inventory-report-page__section">
              <div className="inventory-report-page__section-header">
                <h2>Low-stock follow-up</h2>
                <span>{report.low_stock?.length || 0} rows</span>
              </div>
              {report.low_stock?.length ? (
                <div className="inventory-report-page__cards">
                  {report.low_stock.map((row) => (
                    <article key={row.id} className="inventory-report-page__card">
                      <strong>{row.item?.name}</strong>
                      <p>{row.item?.display_path || "No item path available"}</p>
                      <span>{row.location?.name || "No location"}</span>
                      <span>
                        Qty {formatQuantity(row.quantity_on_hand)} / Threshold{" "}
                        {formatQuantity(row.effective_low_stock_threshold)}
                      </span>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="inventory-report-page__empty">
                  No low-stock rows match the current filters.
                </p>
              )}
            </section>

            <section className="inventory-report-page__section">
              <div className="inventory-report-page__section-header">
                <h2>Stock snapshot</h2>
                <span>{report.stock?.length || 0} rows</span>
              </div>
              <div className="inventory-report-page__table-wrap">
                <table className="inventory-report-page__table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Location</th>
                      <th>Qty</th>
                      <th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.stock?.map((row) => (
                      <tr key={row.id}>
                        <td>{row.item?.display_path || row.item?.name || "N/A"}</td>
                        <td>{row.location?.name || "N/A"}</td>
                        <td>{formatQuantity(row.quantity_on_hand)}</td>
                        <td>{formatCurrency(row.last_unit_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="inventory-report-page__section">
              <div className="inventory-report-page__section-header">
                <h2>Recent transactions</h2>
                <span>{report.recent_transactions?.length || 0} rows</span>
              </div>
              <div className="inventory-report-page__table-wrap">
                <table className="inventory-report-page__table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Type</th>
                      <th>Item</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.recent_transactions?.map((row) => (
                      <tr key={row.id}>
                        <td>{formatDateTime(row.created_at)}</td>
                        <td>{row.transaction_type}</td>
                        <td>{row.item?.display_path || row.item?.name || "N/A"}</td>
                        <td>{formatQuantity(row.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default InventoryReportPage;
